import { useState, useCallback, useRef } from 'react';
import api from './api.js';

/**
 * ページネーション付きAPI取得のカスタムフック
 *
 * @param {string} endpoint - APIエンドポイント (例: '/opportunities/search/')
 * @param {Object} params - 固定パラメータ (例: { is_closed: 'true', owner_id: 'xxx' })
 * @param {number} perPage - 1ページの件数 (デフォルト: 20)
 * @returns {{ items, total, totalPages, page, setPage, loading, error, fetch, perPage }}
 *
 * 使い方:
 *   const active = usePaginatedFetch('/opportunities/search/', { owner_id: user.id, is_closed: 'false' });
 *   // active.items, active.total, active.page, active.setPage, active.loading, active.error
 *   // <Pagination ... onPageChange={active.setPage} />
 */
export default function usePaginatedFetch(endpoint, params = {}, perPage = 20) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);

    // useRef でパラメータを保持し、JSON.stringify の依存を回避
    const paramsRef = useRef(params);
    paramsRef.current = params;

    const endpointRef = useRef(endpoint);
    endpointRef.current = endpoint;

    const fetch = useCallback(async (overridePage) => {
        const p = new URLSearchParams();
        Object.entries(paramsRef.current).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                p.append(key, String(value));
            }
        });
        p.append('page', String(overridePage ?? page));
        p.append('per_page', String(perPage));

        setLoading(true);
        setError(null);
        setForbidden(false);
        try {
            const response = await api.fetch(`${endpointRef.current}?${p.toString()}`);
            if (response && response.ok) {
                const data = await response.json();
                setItems(data.items);
                setTotal(data.total);
                setTotalPages(data.total_pages);
            } else if (response && response.status === 403) {
                // forbidden は state として通知するに留め、エラーメッセージは設定しない。
                // 利用側が forbidden を検知して navigate するので、二重表示・1 フレームの
                // エラー UI を避けるため。
                setForbidden(true);
            } else {
                setError('データの取得に失敗しました。');
            }
        } catch (err) {
            console.error(`Failed to fetch ${endpointRef.current}:`, err);
            setError('通信エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    }, [page, perPage]);

    return { items, total, totalPages, page, setPage, loading, error, forbidden, fetch, perPage };
}
